import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Flag, Field, Button, Chip, Icon } from './primitives.jsx';
import { buildShareTargets } from './share.js';
import { closestExistingName } from './nameMatch.js';

// ============================================================================
// Modal Component: Recover by Email
// ============================================================================

export const RecoverModal = ({ onClose }) => {
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/pool/recover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setDone(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to send recovery email.');
      }
    } catch {
      setError('Network error. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(16,21,79,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <form
        onSubmit={submit}
        style={{
          background: 'var(--paper)', borderRadius: 14,
          padding: 36, maxWidth: 480, width: '100%',
          display: 'flex', flexDirection: 'column', gap: 20,
          boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
        }}
      >
        <div>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 12,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--blue)',
          }}>RECOVER ACCESS</span>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 32,
            letterSpacing: '-0.005em', textTransform: 'uppercase', lineHeight: 1,
            margin: '8px 0 0', color: 'var(--ink)',
          }}>
            FIND YOUR POOLS
          </h2>
        </div>

        {done ? (
          <>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--ink-soft)', lineHeight: 1.55 }}>
              If we have a pool registered under <strong style={{ color: 'var(--ink)' }}>{email}</strong>,
              a recovery email has been sent with all your pool links.
            </p>
            <p style={{ margin: 0, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)', letterSpacing: '0.04em' }}>
              CHECK <code style={{ color: 'var(--blue)' }}>data/emails/</code> FOR THE SIMULATED MESSAGE.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="primary" type="button" onClick={onClose}>DONE</Button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Enter the email you used when creating a pool. We'll send you a
              list of all your pool links.
            </p>
            <Field
              label="Admin email"
              value={email}
              onChange={(val) => { setEmail(val); setError(''); }}
              type="email"
              placeholder="you@company.com"
              error={error}
            />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="secondary" type="button" onClick={onClose} disabled={submitting}>
                CANCEL
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? 'SENDING...' : 'SEND RECOVERY EMAIL'}
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

// ============================================================================
// Modal Component: Score Editing
// ============================================================================

export const ScoreUpdateModal = ({ match, pool, onSave, onClose, adminSecret }) => {
  const tA = pool.teams.find(t => t.id === match.teamA_id);
  const tB = pool.teams.find(t => t.id === match.teamB_id);

  const [scoreA, setScoreA] = React.useState(match.scoreA !== null ? match.scoreA.toString() : '');
  const [scoreB, setScoreB] = React.useState(match.scoreB !== null ? match.scoreB.toString() : '');
  const [winnerId, setWinnerId] = React.useState(match.winner_id || '');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const isKnockout = match.type === 'knockout';
  const showWinnerSelect = isKnockout && scoreA !== '' && scoreB !== '' && parseInt(scoreA, 10) === parseInt(scoreB, 10);

  const handleSave = async (e) => {
    e.preventDefault();
    if (scoreA === '' || scoreB === '') {
      setError('Both scores are required.');
      return;
    }
    const valA = parseInt(scoreA, 10);
    const valB = parseInt(scoreB, 10);
    if (isNaN(valA) || isNaN(valB)) {
      setError('Scores must be integers.');
      return;
    }

    if (isKnockout && valA === valB && !winnerId) {
      setError('Please select a winner (penalty shootout/extra time).');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const res = await fetch(`/api/pool/${pool.id}/matches/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-secret': adminSecret
        },
        body: JSON.stringify({
          matchId: match.id,
          scoreA: valA,
          scoreB: valB,
          winnerId: valA === valB ? winnerId : undefined
        })
      });

      const data = await res.json();
      if (res.ok) {
        onSave(data.pool);
        onClose();
      } else {
        setError(data.error || 'Failed to update score.');
      }
    } catch (err) {
      setError('Network error updating score.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(16,21,79,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24
    }}>
      <form
        onSubmit={handleSave}
        style={{
          background: 'var(--paper)',
          borderRadius: 12,
          padding: 32,
          width: '100%',
          maxWidth: 440,
          border: '2px solid var(--ink)',
          boxShadow: 'var(--shadow-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 24
        }}
      >
        <div>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--blue)'
          }}>{match.type === 'group' ? 'GROUP MATCH' : `KNOCKOUT · ${match.round.toUpperCase()}`}</span>
          <h2 style={{
            fontFamily: 'var(--font-display)', fontSize: 28, textTransform: 'uppercase',
            margin: '8px 0 0', letterSpacing: '-0.005em'
          }}>UPDATE MATCH</h2>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14, fontWeight: 500 }}>{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Flag teamId={tA.id} size={28} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, textTransform: 'uppercase' }}>{tA.country}</span>
          </div>
          <input
            type="number"
            min="0"
            value={scoreA}
            onChange={(e) => setScoreA(e.target.value)}
            style={{
              width: 80, padding: 10, borderRadius: 6,
              border: '1.5px solid var(--hairline-strong)',
              fontFamily: 'var(--font-mono)', fontSize: 18, textAlign: 'center',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Flag teamId={tB.id} size={28} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, textTransform: 'uppercase' }}>{tB.country}</span>
          </div>
          <input
            type="number"
            min="0"
            value={scoreB}
            onChange={(e) => setScoreB(e.target.value)}
            style={{
              width: 80, padding: 10, borderRadius: 6,
              border: '1.5px solid var(--hairline-strong)',
              fontFamily: 'var(--font-mono)', fontSize: 18, textAlign: 'center',
              outline: 'none'
            }}
          />
        </div>

        {showWinnerSelect && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase' }}>CHOOSE WINNER (PENALTIES)</span>
            <select
              value={winnerId}
              onChange={(e) => setWinnerId(e.target.value)}
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 8,
                border: '1.5px solid var(--blue)', background: 'var(--paper)',
                fontFamily: 'var(--font-body)', fontSize: 15, outline: 'none'
              }}
            >
              <option value="">-- SELECT WINNER --</option>
              <option value={tA.id}>{tA.country.toUpperCase()}</option>
              <option value={tB.id}>{tB.country.toUpperCase()}</option>
            </select>
          </label>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="primary" type="submit" disabled={saving} style={{ flex: 1 }}>
            {saving ? 'SAVING...' : 'SAVE SCORE'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            CANCEL
          </Button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Modal Component: Reroll / Assignment reset
// ============================================================================

export const RerollModal = ({ team, pool, onSave, onClose, adminSecret }) => {
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const handleReroll = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/pool/${pool.id}/reroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-secret': adminSecret
        },
        body: JSON.stringify({ teamId: team.id })
      });
      const data = await res.json();
      if (res.ok) {
        onSave(data.pool);
        onClose();
      } else {
        setError(data.error || 'Failed to reset draw.');
      }
    } catch (err) {
      setError('Network error resetting draw.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(16,21,79,0.5)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24
    }}>
      <div style={{
        background: 'var(--paper)',
        border: '2px solid var(--danger)',
        borderRadius: 12,
        padding: 32,
        width: '100%',
        maxWidth: 400,
        boxShadow: 'var(--shadow-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--danger)' }}>DANGER ZONE</span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '8px 0 0' }}>REROLL TEAM DRAW?</h2>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--ink-soft)', fontSize: 15, margin: 0, lineHeight: 1.5 }}>
          This will clear the assignment of <strong>{team.country.toUpperCase()}</strong> to <strong>{team.assigned_to}</strong>.
          The team will become <strong>OPEN</strong> again, allowing anyone to draw it.
        </p>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="danger" onClick={handleReroll} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'RESETTING...' : 'RESET DRAW'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            CANCEL
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Share Modal: QR + copy link + social shares + native Web Share fallback
// ============================================================================

export const ShareModal = ({ pool, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  const qrWrapRef = React.useRef(null);

  const shareUrl = `${window.location.protocol}//${window.location.host}/pool/${pool.id}`;
  const targets = buildShareTargets({ url: shareUrl, poolName: pool.name });
  const shareText = targets.text;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can fail in some embedded webviews; fall back to selection.
      const ta = document.createElement('textarea');
      ta.value = shareUrl; document.body.appendChild(ta);
      ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  const downloadQR = () => {
    const canvas = qrWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `sweep26-${pool.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: `Sweep:WC26 — ${pool.name}`, text: shareText, url: shareUrl });
    } catch {/* user dismissed */}
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  // Short labels so all 4 fit a 4-col grid even when the modal narrows on a
  // 360px phone (each cell ~73px wide — anything longer than "WhatsApp" wraps).
  const socials = [
    { label: 'WHATSAPP', color: '#25D366',    href: targets.whatsapp },
    { label: 'TELEGRAM', color: '#229ED9',    href: targets.telegram },
    { label: 'X',        color: '#000000',    href: targets.twitter },
    { label: 'EMAIL',    color: 'var(--ink)', href: targets.email },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(16,21,79,0.6)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--paper)', borderRadius: 12, padding: 28,
        width: '100%', maxWidth: 460, border: '2px solid var(--ink)',
        boxShadow: 'var(--shadow-3)', display: 'flex', flexDirection: 'column', gap: 20,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>SHARE POOL</span>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '6px 0 0', letterSpacing: '-0.005em' }}>
              {pool.name}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 24, lineHeight: 1, color: 'var(--ink-soft)', padding: 4,
          }}>×</button>
        </div>

        <div
          ref={qrWrapRef}
          style={{
            alignSelf: 'center', background: '#fff', padding: 16, borderRadius: 10,
            border: '1px solid var(--hairline)', lineHeight: 0,
          }}
        >
          <QRCodeCanvas value={shareUrl} size={220} level="M" includeMargin={false} />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--paper-warm)', border: '1px solid var(--hairline)',
          borderRadius: 8, padding: '10px 12px',
        }}>
          <code style={{
            flex: 1, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--blue)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{shareUrl}</code>
          <Button variant="secondary" onClick={copyLink} style={{ padding: '8px 14px', fontSize: 13 }}>
            <Icon name={copied ? 'check' : 'copy'} size={14} /> {copied ? 'COPIED' : 'COPY'}
          </Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {socials.map(s => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: s.color, color: '#fff', textDecoration: 'none',
                padding: '12px 8px', borderRadius: 8, textAlign: 'center',
                fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {s.label}
            </a>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={downloadQR} style={{ flex: 1, minWidth: 140 }}>
            DOWNLOAD QR
          </Button>
          {canNativeShare && (
            <Button variant="primary" onClick={nativeShare} style={{ flex: 1, minWidth: 140 }}>
              MORE APPS…
            </Button>
          )}
        </div>

        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-mute)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em', textAlign: 'center' }}>
          {canNativeShare
            ? "MORE APPS OPENS YOUR PHONE'S SHARE SHEET (INSTAGRAM, TIKTOK, IMESSAGE…)."
            : 'OPEN THIS PAGE ON A PHONE TO SHARE DIRECTLY TO INSTAGRAM, TIKTOK, ETC.'}
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// Admin Modal: Manual Assign — give a specific team (or random) to a name
// ============================================================================

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 1100,
  background: 'rgba(16,21,79,0.6)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
const cardStyle = {
  background: 'var(--paper)', borderRadius: 12, padding: 32,
  width: '100%', maxWidth: 460, border: '2px solid var(--ink)',
  boxShadow: 'var(--shadow-3)', display: 'flex', flexDirection: 'column', gap: 20,
  // Modals render inside the admin console, which sets color:#fff — reset to ink
  // so card headings (which don't set their own colour) stay dark on the paper.
  color: 'var(--ink)',
};

// First-run + later-round draw entry. Resolves WHO is drawing before the draw so
// one human can't become several players by re-typing their name each round:
//   1. device memory  -> greet "Drawing as {name}" (one tap)
//   2. roster picker  -> tap your existing name + confirm
//   3. new player     -> free-type, with a soft "did you mean ...?" guard
// onPick(name, email) resolves to { ok, error }; on success App unmounts this.
export const OnboardModal = ({ pool, assignedCount, onPick, onClose }) => {
  const norm = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');

  // Distinct existing players, first-seen display casing.
  const roster = React.useMemo(() => {
    const seen = new Map();
    for (const t of pool.teams) {
      if (t.assigned_to && !seen.has(norm(t.assigned_to))) seen.set(norm(t.assigned_to), t.assigned_to);
    }
    return [...seen.values()];
  }, [pool.teams]);

  const remembered = (() => {
    try { return localStorage.getItem(`sweep-wc26-player-${pool.id}`) || ''; } catch { return ''; }
  })();

  // 'greet' (device-remembered) | 'roster' (pick existing) | 'new' (type a name)
  const [mode, setMode] = React.useState(remembered ? 'greet' : roster.length > 0 ? 'roster' : 'new');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [dup, setDup] = React.useState(null); // { match } — pending soft-warn confirmation

  const draw = async (chosenName, chosenEmail = '') => {
    if (!chosenName.trim()) { setError('Please enter your name'); return; }
    setError(''); setSubmitting(true);
    const result = await onPick(chosenName.trim(), chosenEmail.trim());
    if (result && !result.ok) {
      setError(result.error || 'Could not draw your team — try again.');
      setSubmitting(false);
    }
    // On success App switches to the walkout screen and this modal unmounts.
  };

  const submitNew = (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name'); return; }
    const match = closestExistingName(name, roster);
    if (match && !dup) { setDup({ match }); return; } // soft guard, once
    draw(name, email);
  };

  const closeBtn = (
    <button type="button" onClick={onClose} aria-label="Close"
      style={{ position: 'absolute', top: 14, right: 14, width: 32, height: 32,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)' }}>
      <Icon name="x" size={20} />
    </button>
  );

  const header = (
    <div>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>
        WELCOME · {assignedCount}/48 CLAIMED
      </span>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 30, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.01em', lineHeight: 0.95 }}>
        {pool.name.toUpperCase()}
      </h2>
    </div>
  );

  const rowBtn = (label, onClick, primary = false) => (
    <button type="button" onClick={onClick} disabled={submitting}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        padding: '14px 16px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
        background: primary ? 'var(--blue)' : 'var(--paper)', color: primary ? 'var(--paper)' : 'var(--ink)',
        border: `2px solid ${primary ? 'var(--blue)' : 'var(--hairline-strong)'}`,
        fontFamily: 'var(--font-display)', fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.02em',
        opacity: submitting ? 0.5 : 1 }}>
      {label} <Icon name="arrow-right" size={16} />
    </button>
  );

  return (
    <div style={overlayStyle}>
      <div style={{ ...cardStyle, maxWidth: 440, position: 'relative' }}>
        {closeBtn}
        {header}

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        {mode === 'greet' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.45 }}>
              Drawing as <strong>{remembered}</strong>. Ready for your next team?
            </p>
            <Button variant="primary" onClick={() => draw(remembered)} disabled={submitting} style={{ width: '100%', fontSize: 18, padding: '16px 26px' }}>
              {submitting ? 'DRAWING…' : <>DRAW MY TEAM <Icon name="arrow-right" size={18} /></>}
            </Button>
            <button type="button" onClick={() => { setError(''); setMode(roster.length > 0 ? 'roster' : 'new'); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)',
                fontFamily: 'var(--font-mono)', fontSize: 13, textDecoration: 'underline' }}>
              Not you?
            </button>
          </div>
        )}

        {mode === 'roster' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14 }}>Who are you? Tap your name.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
              {roster.map((n) => rowBtn(n, () => draw(n)))}
            </div>
            <button type="button" onClick={() => { setError(''); setDup(null); setMode('new'); }}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--blue)',
                fontFamily: 'var(--font-display)', fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '6px 0' }}>
              + I'm new
            </button>
          </div>
        )}

        {mode === 'new' && (
          <>
            <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.45 }}>
              You're about to draw a random World Cup team and follow it through the tournament. One team per person.
            </p>
            {dup ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12,
                background: 'var(--paper-warm)', border: '1.5px solid var(--hairline-strong)', borderRadius: 10, padding: 16 }}>
                <span style={{ fontSize: 14, color: 'var(--ink)' }}>
                  Did you mean <strong>{dup.match}</strong>? Pick your existing name so your teams stay together.
                </span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button variant="primary" onClick={() => draw(dup.match)} disabled={submitting} style={{ flex: 1 }}>
                    THAT'S ME
                  </Button>
                  <Button variant="secondary" onClick={() => draw(name, email)} disabled={submitting}>
                    NO, I'M NEW
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submitNew} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field label="Your name" value={name} onChange={(v) => { setName(v); setError(''); }} placeholder="e.g. Luke" />
                <Field label="Email (optional)" value={email} onChange={setEmail} placeholder="you@work.com" type="email"
                  hint="We'll send your team so you can show it off" />
                <Button variant="primary" type="submit" disabled={submitting} style={{ width: '100%', fontSize: 18, padding: '16px 26px' }}>
                  {submitting ? 'DRAWING…' : <>DRAW MY TEAM <Icon name="arrow-right" size={18} /></>}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const ManualAssignModal = ({ pool, adminSecret, onClose, onUpdatePool }) => {
  const [name, setName] = React.useState('');
  const [teamId, setTeamId] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const available = pool.teams.filter(t => !t.assigned_to);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Enter a name.'); return; }
    setError(''); setSubmitting(true);
    try {
      const body = { name: name.trim() };
      if (teamId) body.teamId = teamId;
      const res = await fetch(`/api/pool/${pool.id}/assignments/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'admin-secret': adminSecret },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to assign.'); return; }
      onUpdatePool(data.pool);
      onClose();
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>ADMIN</span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.005em' }}>
            ASSIGN TEAM
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
            {available.length} team{available.length === 1 ? '' : 's'} unassigned. Leave team as "Random" to pick one for them.
          </p>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        <Field
          label="Participant name"
          value={name}
          onChange={setName}
          placeholder="e.g. Sarah"
        />

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink)' }}>Team</span>
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            style={{
              background: 'var(--paper)',
              border: '1.5px solid var(--hairline-strong)',
              borderRadius: 8, padding: '14px 16px',
              fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--ink)',
              outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">🎲 Random from remaining</option>
            {available
              .slice()
              .sort((a, b) => a.group.localeCompare(b.group) || a.country.localeCompare(b.country))
              .map(t => (
                <option key={t.id} value={t.id}>
                  Group {t.group} · {t.country}
                </option>
              ))}
          </select>
        </label>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="primary" type="submit" style={{ flex: 1 }} disabled={submitting || available.length === 0}>
            {submitting ? 'ASSIGNING…' : 'ASSIGN'}
          </Button>
          <Button variant="secondary" onClick={onClose}>CANCEL</Button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Admin Modal: Auto-Fill Remaining — shuffle remaining teams across names
// ============================================================================

export const AutoFillModal = ({ pool, adminSecret, onClose, onUpdatePool }) => {
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const availableCount = pool.teams.filter(t => !t.assigned_to).length;
  const names = text.split('\n').map(s => s.trim()).filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (names.length === 0) { setError('Enter at least one name.'); return; }
    if (names.length > availableCount) {
      setError(`Only ${availableCount} teams left but ${names.length} names given.`);
      return;
    }
    setError(''); setSubmitting(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/assignments/auto-fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'admin-secret': adminSecret },
        body: JSON.stringify({ names }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to auto-fill.'); return; }
      onUpdatePool(data.pool);
      onClose();
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>ADMIN</span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.005em' }}>
            AUTO-FILL REMAINING
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
            One name per line. Each gets a random team from the {availableCount} unassigned.
          </p>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink)' }}>
            Names ({names.length} / {availableCount})
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Sarah\nTom\nRiya'}
            rows={6}
            style={{
              background: 'var(--paper)',
              border: '1.5px solid var(--hairline-strong)',
              borderRadius: 8, padding: '14px 16px',
              fontFamily: 'var(--font-mono)', fontSize: 15, color: 'var(--ink)',
              outline: 'none', resize: 'vertical',
            }}
          />
        </label>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="primary" type="submit" style={{ flex: 1 }} disabled={submitting || availableCount === 0}>
            {submitting ? 'FILLING…' : `FILL ${names.length} SLOT${names.length === 1 ? '' : 'S'}`}
          </Button>
          <Button variant="secondary" onClick={onClose}>CANCEL</Button>
        </div>
      </form>
    </div>
  );
};

// Admin: resize a running pool — players (how many people) and rounds (teams
// each drafts). Raising players admits latecomers; raising rounds gives everyone
// another draw and reopens a finished draft. Neither can drop below what's
// already been used, and players × rounds is capped at the 48-team total.
const selectStyle = {
  background: 'var(--paper)', border: '1.5px solid var(--hairline-strong)',
  borderRadius: 8, padding: '14px 16px',
  fontFamily: 'var(--font-body)', fontSize: 16, color: 'var(--ink)', outline: 'none',
};
const labelStyle = {
  fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: 'var(--ink)',
};

export const EditPoolModal = ({ pool, adminSecret, onClose, onUpdatePool }) => {
  const norm = (s) => s.trim().toLowerCase();
  const currentPlayers = pool.num_players ?? 1;
  const currentRounds = pool.rounds ?? 1;

  const enrolledCount = new Set(
    pool.teams.filter(t => t.assigned_to).map(t => norm(t.assigned_to))
  ).size;
  const highestRoundDrawn = pool.teams.reduce(
    (max, t) => (t.assigned_to && t.round > max ? t.round : max), 0
  );
  const minPlayers = Math.max(1, enrolledCount);
  const minRounds = Math.max(1, highestRoundDrawn);

  const [players, setPlayers] = React.useState(currentPlayers);
  const [rounds, setRounds] = React.useState(currentRounds);
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  // Players and rounds trade off against the 48-team cap, so changing one clamps
  // the other down if their product would overflow (never below its own floor).
  const onPlayersChange = (p) => {
    setPlayers(p);
    const maxR = Math.floor(48 / p);
    if (rounds > maxR) setRounds(Math.max(minRounds, maxR));
  };
  const onRoundsChange = (r) => {
    setRounds(r);
    const maxP = Math.floor(48 / r);
    if (players > maxP) setPlayers(Math.max(minPlayers, maxP));
  };

  const maxPlayers = Math.floor(48 / rounds);
  const maxRounds = Math.floor(48 / players);
  const teamsUsed = players * rounds;
  const changed = players !== currentPlayers || rounds !== currentRounds;

  const range = (lo, hi) => Array.from({ length: Math.max(0, hi - lo + 1) }, (_, i) => lo + i);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'admin-secret': adminSecret },
        body: JSON.stringify({ numPlayers: players, rounds }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to update the pool.'); return; }
      onUpdatePool(data.pool);
      onClose();
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>ADMIN</span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.005em' }}>
            EDIT POOL
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
            Players is how many people can draw; rounds is how many teams each drafts. Raise players to let latecomers in, or rounds to give everyone another team — they share the 48-team total, and a latecomer simply ends up with fewer teams.
          </p>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 16 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            <span style={labelStyle}>Players</span>
            <select value={players} onChange={(e) => onPlayersChange(Number(e.target.value))} style={selectStyle}>
              {range(minPlayers, maxPlayers).map(n => (
                <option key={n} value={n}>{n}{n === currentPlayers ? ' (now)' : ''}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
            <span style={labelStyle}>Rounds</span>
            <select value={rounds} onChange={(e) => onRoundsChange(Number(e.target.value))} style={selectStyle}>
              {range(minRounds, maxRounds).map(n => (
                <option key={n} value={n}>{n}{n === currentRounds ? ' (now)' : ''}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Quick "add a round" affordance: bump rounds by one (then SAVE), enabled
            while another full round still fits inside the 48-team pool. At the cap
            we explain why and how to consume the leftover teams. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            onClick={() => onRoundsChange(rounds + 1)}
            disabled={rounds >= maxRounds}
          >
            + ADD A ROUND
          </Button>
          <span style={{ fontSize: 12, color: 'var(--ink-mute)', flex: 1, minWidth: 180 }}>
            {rounds < maxRounds
              ? `Adds round ${rounds + 1} — every player drafts another team.`
              : teamsUsed === 48
                ? 'Every one of the 48 teams is in play — the pool is full.'
                : `Only ${48 - teamsUsed} team${48 - teamsUsed === 1 ? '' : 's'} left — not enough for another full round of ${players}. Add players or assign the spare team${48 - teamsUsed === 1 ? '' : 's'} by hand to use all 48.`}
          </span>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontFamily: 'var(--font-mono)', fontSize: 13, letterSpacing: '0.04em',
          color: teamsUsed > 48 ? 'var(--danger)' : 'var(--ink-soft)',
        }}>
          <span>{players} PLAYER{players === 1 ? '' : 'S'} × {rounds} ROUND{rounds === 1 ? '' : 'S'}</span>
          <span>{teamsUsed} / 48 TEAMS</span>
        </div>

        {(minPlayers > 1 || minRounds > 1) && (
          <p style={{ margin: 0, color: 'var(--ink-mute)', fontSize: 12 }}>
            Can't go below {minPlayers} player{minPlayers === 1 ? '' : 's'} or {minRounds} round{minRounds === 1 ? '' : 's'} — that's what's already been drawn.
          </p>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="primary" type="submit" style={{ flex: 1 }} disabled={submitting || !changed}>
            {submitting ? 'SAVING…' : changed ? 'SAVE CHANGES' : 'NO CHANGE'}
          </Button>
          <Button variant="secondary" onClick={onClose}>CANCEL</Button>
        </div>
      </form>
    </div>
  );
};

// One-time "what's new" briefing for admins. Bump WHATS_NEW_VERSION to re-show
// it after the next batch of changes; dismissal is remembered per browser.
export const WHATS_NEW_VERSION = '2026-06';

const WHATS_NEW_ITEMS = [
  ['menu', 'MOBILE, REBUILT', 'The site now works properly on phones. Tap ☰ for the menu, and collapse this console with the ⌄ button to free up screen space.'],
  ['play', 'START ROUNDS YOURSELF', 'New START ROUND button — open the next round early instead of waiting for every player to draw.'],
  ['edit', 'RESIZE YOUR POOL', 'New EDIT POOL button — change players or rounds anytime. Let latecomers join, or give everyone more teams (up to the 48-team total).'],
];

export const WhatsNewModal = ({ onClose }) => (
  <div style={overlayStyle}>
    <div style={{ ...cardStyle, maxWidth: 480, gap: 16 }}>
      <div>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>
          JUST SHIPPED
        </span>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 30, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.01em', lineHeight: 0.95 }}>
          WHAT'S NEW
        </h2>
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 14 }}>
          A few upgrades to how you run your pool as the admin.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {WHATS_NEW_ITEMS.map(([icon, title, body]) => (
          <div key={title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 10,
              background: 'var(--blue-pale)', color: 'var(--blue)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name={icon} size={20} />
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink)' }}>
                {title}
              </div>
              <p style={{ margin: '3px 0 0', color: 'var(--ink-soft)', fontSize: 13.5, lineHeight: 1.4 }}>{body}</p>
            </div>
          </div>
        ))}
      </div>

      <p style={{ margin: 0, color: 'var(--ink-mute)', fontSize: 12, fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
        EVERYTHING LIVES IN THE CONSOLE AT THE BOTTOM OF YOUR SCREEN.
      </p>

      <Button variant="primary" onClick={onClose} style={{ width: '100%' }}>GOT IT</Button>
    </div>
  </div>
);

// ============================================================================
// Player launch tour — a guided "walkaround" for ALL pool members (new and
// returning) so they discover the side-games. A bottom-anchored card whose
// content matches the screen behind it; App.jsx navigates to each step's screen
// as you move through. Version-gated (TOUR_VERSION) and re-launchable from the
// dashboard. Distinct from the admin-only WhatsNewModal above.
// ============================================================================
export const TOUR_VERSION = '2026-06';

// Each step names the screen App should be on and the copy for its card.
export const TOUR_STEPS = [
  { screen: 'dashboard', title: "YOU'RE IN", body: "Welcome aboard. Here's a 30-second walk through everything you can play — including two brand-new side-games." },
  { screen: 'eliminator', isNew: true, title: 'ELIMINATOR', body: 'Last man standing. Pick a team to win each round — no team twice. Two lives in the group, sudden death in the knockouts. Outlast everyone.' },
  { screen: 'predictor', title: 'PREDICTOR', body: 'Call the score of every match. Super-6 scoring — 5 points for an exact result. Its own scoreboard, running all tournament.' },
  { screen: 'dashboard', title: 'YOUR TEAM', body: 'Back here on the dashboard you follow your drawn team, watch live results land and see the group tables take shape.' },
  { screen: 'dashboard', title: 'ALL 48 TEAMS', body: "Scroll down to see every team, who drew them and how they're doing. That's the tour — good luck out there." },
];

export const TourModal = ({ step, onBack, onNext, onSkip }) => {
  const s = TOUR_STEPS[step];
  if (!s) return null;
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1200,
      display: 'flex', justifyContent: 'center', padding: 'clamp(12px, 4vw, 28px)',
      pointerEvents: 'none', // let the live screen above stay visible; only the card is interactive
    }}>
      <div style={{
        pointerEvents: 'auto', width: '100%', maxWidth: 560,
        background: 'var(--paper)', border: '2px solid var(--ink)', borderRadius: 14,
        boxShadow: 'var(--shadow-3)', padding: 'clamp(18px, 4vw, 24px)',
        display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--ink)',
      }}>
        {/* progress dots + skip */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 7 }}>
            {TOUR_STEPS.map((_, i) => (
              <span key={i} style={{
                width: i === step ? 22 : 8, height: 8, borderRadius: 999,
                background: i === step ? 'var(--blue)' : 'var(--ink-faint)',
                transition: 'width 200ms var(--ease-out)',
              }} />
            ))}
          </div>
          <button type="button" onClick={onSkip} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--ink-mute)',
          }}>Skip tour</button>
        </div>

        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {`Step ${step + 1} of ${TOUR_STEPS.length}`}
            {s.isNew && (
              <span style={{ background: 'var(--gold)', color: 'var(--ink)', fontSize: 9, letterSpacing: '0.12em', padding: '2px 6px', borderRadius: 999, lineHeight: 1 }}>NEW</span>
            )}
          </span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, textTransform: 'uppercase', margin: '6px 0 0', letterSpacing: '-0.01em', lineHeight: 0.95 }}>
            {s.title}
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.45 }}>{s.body}</p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {!isFirst && (
            <Button variant="secondary" onClick={onBack} style={{ whiteSpace: 'nowrap' }}>
              <Icon name="arrow-left" size={14} /> BACK
            </Button>
          )}
          <span style={{ flex: 1 }} />
          <Button variant="primary" onClick={onNext} style={{ whiteSpace: 'nowrap' }}>
            {isLast ? 'GOT IT' : <>SHOW ME <Icon name="arrow-right" size={16} /></>}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Admin: rename a player, or merge them into an existing player by renaming to
// that name. Fixes duplicate/misspelled identities from re-typed names.
export const RenamePlayerModal = ({ pool, adminSecret, onClose, onUpdatePool }) => {
  const norm = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const roster = React.useMemo(() => {
    const seen = new Map();
    for (const t of pool.teams) {
      if (t.assigned_to && !seen.has(norm(t.assigned_to))) seen.set(norm(t.assigned_to), t.assigned_to);
    }
    return [...seen.values()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [pool.teams]);

  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const merges = to.trim() && roster.some(n => norm(n) === norm(to) && norm(n) !== norm(from));

  const submit = async (e) => {
    e.preventDefault();
    if (!from.trim() || !to.trim()) { setError('Pick a player and enter the corrected name.'); return; }
    if (norm(from) === norm(to)) { setError('The new name matches the current name.'); return; }
    setError(''); setSubmitting(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/players/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'admin-secret': adminSecret },
        body: JSON.stringify({ from: from.trim(), to: to.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to rename.'); return; }
      onUpdatePool(data.pool);
      onClose();
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <form onSubmit={submit} style={cardStyle}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>ADMIN</span>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, textTransform: 'uppercase', margin: '8px 0 0', letterSpacing: '-0.005em' }}>
            RENAME / MERGE PLAYER
          </h2>
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)', fontSize: 13 }}>
            Fix a misspelled name, or merge a duplicate by renaming it to the correct player.
          </p>
        </div>

        {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={labelStyle}>Player to fix</span>
          <select value={from} onChange={(e) => setFrom(e.target.value)} style={selectStyle}>
            <option value="">Select a player...</option>
            {roster.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>

        <Field label="Corrected name" value={to} onChange={setTo} placeholder="e.g. Jeevesy" />

        {merges && (
          <div style={{ fontSize: 13, color: 'var(--ink-soft)', background: 'var(--paper-warm)', border: '1.5px solid var(--hairline-strong)', borderRadius: 8, padding: '10px 12px' }}>
            <strong>{to.trim()}</strong> already exists — their teams and picks will be merged into one.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button variant="primary" type="submit" style={{ flex: 1 }} disabled={submitting || !from || !to.trim()}>
            {submitting ? 'SAVING...' : merges ? 'MERGE PLAYERS' : 'RENAME'}
          </Button>
          <Button variant="secondary" onClick={onClose}>CANCEL</Button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Layout: Bottom Sticky Admin Console Panel
// ============================================================================

export const AdminPanel = ({ pool, adminSecret, onUnlock, onUpdatePool }) => {
  const [inputSecret, setInputSecret] = React.useState('');
  const [error, setError] = React.useState('');
  const [openUnlockModal, setOpenUnlockModal] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [openManual, setOpenManual] = React.useState(false);
  const [openAutoFill, setOpenAutoFill] = React.useState(false);
  const [openSettings, setOpenSettings] = React.useState(false);
  const [openRename, setOpenRename] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [showWhatsNew, setShowWhatsNew] = React.useState(false);

  // White-label lead/winner export controls (handle pools only).
  const [exportMode, setExportMode] = React.useState('all'); // all | top | reached
  const [topN, setTopN] = React.useState(3);
  const [reached, setReached] = React.useState('r16');

  const download = async (url, filename) => {
    const res = await fetch(url, { headers: { 'admin-secret': adminSecret } });
    if (!res.ok) return;
    const blob = await res.blob();
    const a = document.createElement('a');
    const href = URL.createObjectURL(blob);
    a.href = href; a.download = filename; a.click();
    URL.revokeObjectURL(href);
  };

  const exportLeads = () => download(`/api/pool/${pool.id}/entrants?format=csv`, `${pool.id}-leads.csv`);
  const exportWinners = () => {
    const params = new URLSearchParams({ format: 'csv' });
    if (exportMode === 'top') params.set('top', String(topN));
    if (exportMode === 'reached') params.set('reached', reached);
    download(`/api/pool/${pool.id}/eliminator/entrants?${params}`, `${pool.id}-winners.csv`);
  };

  // Show the "what's new" briefing once per admin (per browser) when the console
  // is unlocked. Bumping WHATS_NEW_VERSION re-shows it after the next release.
  React.useEffect(() => {
    if (adminSecret && !localStorage.getItem('sweep-wc26-whatsnew-' + WHATS_NEW_VERSION)) {
      setShowWhatsNew(true);
    }
  }, [adminSecret]);
  const dismissWhatsNew = () => {
    try { localStorage.setItem('sweep-wc26-whatsnew-' + WHATS_NEW_VERSION, '1'); } catch { /* private mode */ }
    setShowWhatsNew(false);
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!inputSecret.trim()) return;
    setError('');

    try {
      const res = await fetch(`/api/pool/${pool.id}/admin`, {
        headers: { 'admin-secret': inputSecret.trim() }
      });
      if (res.ok) {
        onUnlock(inputSecret.trim());
        setOpenUnlockModal(false);
        setInputSecret('');
      } else {
        setError('Invalid admin secret code');
      }
    } catch (err) {
      setError('Network validation error');
    }
  };

  const handleSimulateGroups = async () => {
    if (!confirm('Simulate all remaining group matches? This will run scores automatically.')) return;
    setRunning(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/matches/simulate-group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-secret': adminSecret
        }
      });
      const data = await res.json();
      if (res.ok) {
        onUpdatePool(data.pool);
      } else {
        alert(data.error || 'Failed to simulate matches.');
      }
    } catch (err) {
      alert('Network error simulating matches.');
    } finally {
      setRunning(false);
    }
  };

  const handleAdvanceRound = async () => {
    const nextRound = (pool.current_round ?? 1) + 1;
    if (!confirm(`Start Round ${nextRound} now? Players who haven't drawn this round will miss it. This can't be undone.`)) return;
    setRunning(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/round/advance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-secret': adminSecret
        }
      });
      const data = await res.json();
      if (res.ok) {
        onUpdatePool(data.pool);
      } else {
        alert(data.error || 'Failed to start the next round.');
      }
    } catch (err) {
      alert('Network error starting the next round.');
    } finally {
      setRunning(false);
    }
  };

  const handleAddRound = async () => {
    const newRounds = (pool.rounds ?? 1) + 1;
    const draftDone = (pool.current_round ?? 1) > (pool.rounds ?? 1);
    const remaining = pool.teams?.filter(t => !t.assigned_to).length ?? 0;
    const msg = draftDone && remaining > 0
      ? `Open Round ${newRounds}? ${remaining} unassigned team${remaining === 1 ? '' : 's'} will become available to draw.`
      : `Add round ${newRounds}? Every player will need to pick one more team.`;
    if (!confirm(msg)) return;
    setRunning(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'admin-secret': adminSecret },
        body: JSON.stringify({ rounds: newRounds }),
      });
      const data = await res.json();
      if (res.ok) { onUpdatePool(data.pool); }
      else { alert(data.error || 'Could not add round.'); }
    } catch { alert('Network error.'); } finally { setRunning(false); }
  };

  const handleTransitionBracket = async () => {
    if (!confirm('Calculate standings and generate the Round of 32 bracket? This locks group play.')) return;
    setRunning(true);
    try {
      const res = await fetch(`/api/pool/${pool.id}/bracket/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-secret': adminSecret
        }
      });
      const data = await res.json();
      if (res.ok) {
        onUpdatePool(data.pool);
      } else {
        alert(data.error || 'Failed to start playoffs.');
      }
    } catch (err) {
      alert('Network error starting playoffs.');
    } finally {
      setRunning(false);
    }
  };

  if (!adminSecret) {
    return (
      <>
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          background: 'rgba(16,21,79,0.95)', borderTop: '2px solid var(--blue)',
          padding: '16px 32px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', color: '#fff', backdropFilter: 'blur(8px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)' }}>SWEEP:WC26 CONSOLE</span>
            <Chip tone="warning">LOCKED</Chip>
          </div>
          <Button variant="onInk" onClick={() => setOpenUnlockModal(true)}>
            UNLOCK ADMIN PANEL <Icon name="lock" size={14} />
          </Button>
        </div>

        {openUnlockModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(16,21,79,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24
          }}>
            <form
              onSubmit={handleUnlock}
              style={{
                background: 'var(--paper)', borderRadius: 12, padding: 32,
                width: '100%', maxWidth: 400, border: '2px solid var(--ink)',
                boxShadow: 'var(--shadow-3)', display: 'flex', flexDirection: 'column', gap: 20,
                color: 'var(--ink)',
              }}
            >
              <div>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--blue)' }}>AUTHENTICATE</span>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, textTransform: 'uppercase', margin: '8px 0 0' }}>ENTER SECRET</h2>
              </div>

              {error && <div style={{ color: 'var(--danger)', fontSize: 14 }}>{error}</div>}

              <Field
                label="Admin Secret Code"
                value={inputSecret}
                onChange={setInputSecret}
                placeholder="e.g. abcd1234efgh"
                type="password"
              />

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <Button variant="primary" type="submit" style={{ flex: 1 }}>UNLOCK</Button>
                <Button variant="secondary" onClick={() => { setOpenUnlockModal(false); setInputSecret(''); setError(''); }}>CANCEL</Button>
              </div>
            </form>
          </div>
        )}
      </>
    );
  }

  const hasNextRound = (pool.current_round ?? 1) < (pool.rounds ?? 1);
  const draftComplete = (pool.current_round ?? 1) > (pool.rounds ?? 1);
  const unassignedCount = pool.teams?.filter(t => !t.assigned_to).length ?? 0;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: 'var(--ink)', borderTop: '3px solid var(--blue)',
      padding: '12px clamp(16px, 4vw, 32px)', display: 'flex', justifyContent: 'space-between',
      alignItems: 'center', color: '#fff', flexWrap: 'wrap', gap: 16
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)' }}>SWEEP:WC26 CONSOLE</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--blue)' }}>KEY: {adminSecret}</span>
          </div>
        </div>
        <Chip tone="success">UNLOCKED</Chip>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {!collapsed && (
          <>
            {pool.stage === 'group' && pool.identity_mode !== 'handle' && (
              <>
                <Button variant="secondary" onClick={() => setOpenManual(true)} disabled={running} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                  ASSIGN TEAM
                </Button>
                <Button variant="secondary" onClick={() => setOpenAutoFill(true)} disabled={running} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                  AUTO-FILL
                </Button>
                <Button variant="secondary" onClick={() => setOpenSettings(true)} disabled={running} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                  EDIT POOL
                </Button>
                <Button variant="secondary" onClick={() => setOpenRename(true)} disabled={running} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                  RENAME / MERGE PLAYER
                </Button>
                {hasNextRound && (
                  <Button variant="secondary" onClick={handleAdvanceRound} disabled={running} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                    START ROUND {(pool.current_round ?? 1) + 1} <Icon name="arrow-right" size={14} />
                  </Button>
                )}
                {draftComplete && unassignedCount > 0 ? (
                  <Button variant="primary" onClick={handleAddRound} disabled={running} title={`Open round ${(pool.rounds ?? 1) + 1} — ${unassignedCount} unassigned team${unassignedCount === 1 ? '' : 's'} will be available to draw`}>
                    OPEN ROUND {(pool.rounds ?? 1) + 1} <Icon name="arrow-right" size={14} />
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={handleAddRound} disabled={running || (pool.num_players ?? 0) * ((pool.rounds ?? 1) + 1) > 48} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} title={(pool.num_players ?? 0) * ((pool.rounds ?? 1) + 1) > 48 ? `Cap reached — need fewer than ${Math.ceil(49 / ((pool.rounds ?? 1) + 1))} players to add another round` : `Add round ${(pool.rounds ?? 1) + 1}`}>
                    ADD A ROUND <Icon name="plus" size={14} />
                  </Button>
                )}
              </>
            )}
            {pool.identity_mode === 'handle' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Export</span>
                <Button variant="secondary" onClick={exportLeads} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                  DOWNLOAD LEADS (CSV)
                </Button>
                {(pool.enabled_games || []).includes('eliminator') && (
                  <>
                    <select value={exportMode} onChange={(e) => setExportMode(e.target.value)}
                      style={{ background: 'var(--ink)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                      <option value="all">All entrants (ranked)</option>
                      <option value="top">Top N</option>
                      <option value="reached">Reached round ≥</option>
                    </select>
                    {exportMode === 'top' && (
                      <input type="number" min={1} value={topN} onChange={(e) => setTopN(Number(e.target.value))}
                        style={{ width: 72, background: 'var(--ink)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: 14 }} />
                    )}
                    {exportMode === 'reached' && (
                      <select value={reached} onChange={(e) => setReached(e.target.value)}
                        style={{ background: 'var(--ink)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8, padding: '10px 12px', fontFamily: 'var(--font-body)', fontSize: 14 }}>
                        {['g1','g2','g3','r32','r16','qf','sf','f'].map((k) => <option key={k} value={k}>{k.toUpperCase()}</option>)}
                      </select>
                    )}
                    <Button variant="secondary" onClick={exportWinners} style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }}>
                      DOWNLOAD ELIMINATOR WINNERS (CSV)
                    </Button>
                  </>
                )}
              </div>
            )}
            {pool.stage === 'bracket' && (
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center' }}>
                CLICK BRACKET MATCHES TO UPDATE SCORES
              </span>
            )}
            <Button variant="secondary" onClick={() => onUnlock(null)} style={{ color: 'var(--ink-mute)', borderColor: 'rgba(255,255,255,0.2)' }}>
              LOCK
            </Button>
          </>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expand console' : 'Collapse console'}
          aria-expanded={!collapsed}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 44, height: 44, borderRadius: 10, cursor: 'pointer',
            background: 'transparent', border: '1.5px solid rgba(255,255,255,0.3)',
            color: '#fff', flexShrink: 0,
          }}
        >
          <Icon name={collapsed ? 'chevron-up' : 'chevron-down'} size={20} />
        </button>
      </div>

      {openManual && (
        <ManualAssignModal
          pool={pool}
          adminSecret={adminSecret}
          onClose={() => setOpenManual(false)}
          onUpdatePool={onUpdatePool}
        />
      )}
      {openAutoFill && (
        <AutoFillModal
          pool={pool}
          adminSecret={adminSecret}
          onClose={() => setOpenAutoFill(false)}
          onUpdatePool={onUpdatePool}
        />
      )}
      {openSettings && (
        <EditPoolModal
          pool={pool}
          adminSecret={adminSecret}
          onClose={() => setOpenSettings(false)}
          onUpdatePool={onUpdatePool}
        />
      )}
      {openRename && (
        <RenamePlayerModal
          pool={pool}
          adminSecret={adminSecret}
          onClose={() => setOpenRename(false)}
          onUpdatePool={onUpdatePool}
        />
      )}
      {showWhatsNew && <WhatsNewModal onClose={dismissWhatsNew} />}
    </div>
  );
};
